import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Clock, UserX, Loader2, Search, CheckCircle, Circle, PlayCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { apiClient } from '@/services/apiClient';
import type { OpenItem } from '../../../api/onboardingApi';
import * as api from '../../../api/onboardingApi';

const reasonConfig: Record<string, { label: string; icon: React.ReactNode; badgeVariant: 'destructive' | 'secondary' | 'outline' | 'default' }> = {
  blocked: { label: 'Blocked', icon: <AlertTriangle className="h-3 w-3" />, badgeVariant: 'destructive' },
  waiting_customer: { label: 'Waiting on Customer', icon: <UserX className="h-3 w-3" />, badgeVariant: 'secondary' },
  overdue: { label: 'Overdue', icon: <Clock className="h-3 w-3" />, badgeVariant: 'destructive' },
  not_started: { label: 'Not Started', icon: <Circle className="h-3 w-3" />, badgeVariant: 'outline' },
  in_progress: { label: 'In Progress', icon: <PlayCircle className="h-3 w-3" />, badgeVariant: 'default' },
};

interface AllTask {
  id: number;
  plan_id: number;
  name: string;
  status: string;
  due_date: string | null;
  blocker_reason: string | null;
  owner_user_id: number | null;
  owner_name: string | null;
  customer_org_id: number;
  customer_org_name: string | null;
  csm_user_id: number | null;
  csm_name: string | null;
  phase_name: string | null;
  aging_days: number;
  reason: string;
  priority?: string;
}

export default function OpenItems() {
  const { t } = useAppTranslation();
  const [flaggedItems, setFlaggedItems] = useState<OpenItem[]>([]);
  const [allTasks, setAllTasks] = useState<AllTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'flagged' | 'all'>('flagged');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [flagged, plansRes] = await Promise.all([
        api.listOpenItems(),
        api.listPlans({ status: 'active' }),
      ]);
      setFlaggedItems(flagged);

      // Fetch all incomplete tasks from active plans
      const allIncomplete: AllTask[] = [];
      for (const plan of plansRes) {
        try {
          const planData = await api.getPlan(plan.id);
          const incompleteTasks = planData.tasks
            .filter((t: any) => !['done', 'skipped'].includes(t.status))
            .map((t: any) => ({
              id: t.id,
              plan_id: plan.id,
              name: t.name,
              status: t.status,
              due_date: t.due_date,
              blocker_reason: t.blocker_reason,
              owner_user_id: t.owner_user_id,
              owner_name: t.owner_name || null,
              customer_org_id: plan.customer_org_id,
              customer_org_name: plan.customer_org_name || null,
              csm_user_id: plan.csm_user_id,
              csm_name: plan.csm_user_name || null,
              phase_name: null,
              aging_days: t.due_date && new Date(t.due_date) < new Date() ? Math.floor((Date.now() - new Date(t.due_date).getTime()) / 86400000) : 0,
              reason: t.status === 'blocked' ? 'blocked' : t.status === 'waiting_customer' ? 'waiting_customer' : (t.due_date && new Date(t.due_date) < new Date() ? 'overdue' : t.status),
              priority: t.priority,
            }));
          allIncomplete.push(...incompleteTasks);
        } catch (err) { /* skip failed plan fetches */ }
      }
      setAllTasks(allIncomplete);
    } catch (err) { console.error('Error fetching open items:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const items = view === 'flagged' ? flaggedItems : allTasks;
  const filtered = items.filter(item => {
    if (filterStatus && item.status !== filterStatus && (item as any).reason !== filterStatus) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return item.name.toLowerCase().includes(q) || (item.customer_org_name || '').toLowerCase().includes(q) || (item.owner_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const blockedCount = flaggedItems.filter(i => i.reason === 'blocked').length;
  const waitingCount = flaggedItems.filter(i => i.reason === 'waiting_customer').length;
  const overdueCount = flaggedItems.filter(i => i.reason === 'overdue').length;
  const notStartedCount = allTasks.filter(i => i.status === 'not_started').length;
  const inProgressCount = allTasks.filter(i => i.status === 'in_progress').length;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{t('onboarding.openItems.title', 'Open Items')}</h2>
        <p className="text-sm text-gray-500">All incomplete tasks across active onboarding plans</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="bg-red-50 border-red-200 cursor-pointer hover:shadow-sm" onClick={() => { setView('flagged'); setFilterStatus('blocked'); }}>
          <CardContent className="pt-4 pb-3">
            <AlertTriangle className="h-4 w-4 text-red-600 mb-1" />
            <p className="text-xs text-red-700">Blocked</p>
            <p className="text-xl font-bold text-red-800">{blockedCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200 cursor-pointer hover:shadow-sm" onClick={() => { setView('flagged'); setFilterStatus('waiting_customer'); }}>
          <CardContent className="pt-4 pb-3">
            <UserX className="h-4 w-4 text-yellow-600 mb-1" />
            <p className="text-xs text-yellow-700">Waiting</p>
            <p className="text-xl font-bold text-yellow-800">{waitingCount}</p>
          </CardContent>
        </Card>
        <Card className="bg-orange-50 border-orange-200 cursor-pointer hover:shadow-sm" onClick={() => { setView('flagged'); setFilterStatus('overdue'); }}>
          <CardContent className="pt-4 pb-3">
            <Clock className="h-4 w-4 text-orange-600 mb-1" />
            <p className="text-xs text-orange-700">Overdue</p>
            <p className="text-xl font-bold text-orange-800">{overdueCount}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-sm" onClick={() => { setView('all'); setFilterStatus('not_started'); }}>
          <CardContent className="pt-4 pb-3">
            <Circle className="h-4 w-4 text-gray-500 mb-1" />
            <p className="text-xs text-gray-600">Not Started</p>
            <p className="text-xl font-bold">{notStartedCount}</p>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-sm" onClick={() => { setView('all'); setFilterStatus('in_progress'); }}>
          <CardContent className="pt-4 pb-3">
            <PlayCircle className="h-4 w-4 text-blue-500 mb-1" />
            <p className="text-xs text-blue-600">In Progress</p>
            <p className="text-xl font-bold">{inProgressCount}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <div className="flex border rounded-md overflow-hidden text-sm">
          <button onClick={() => { setView('flagged'); setFilterStatus(''); }} className={`px-3 py-1.5 ${view === 'flagged' ? 'bg-red-50 text-red-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
            Flagged ({flaggedItems.length})
          </button>
          <button onClick={() => { setView('all'); setFilterStatus(''); }} className={`px-3 py-1.5 ${view === 'all' ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
            All Open ({allTasks.length})
          </button>
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2 h-4 w-4 text-gray-400" />
          <Input className="pl-9 h-8 text-sm" placeholder="Search tasks, customers..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-36 h-8 text-xs"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="">All statuses</SelectItem>
            <SelectItem value="blocked">Blocked</SelectItem>
            <SelectItem value="waiting_customer">Waiting</SelectItem>
            <SelectItem value="not_started">Not Started</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Items list */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Open Tasks ({filtered.length})</CardTitle></CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="text-center text-gray-400 py-8">{view === 'flagged' ? 'No flagged items — all clear!' : 'No open tasks match your filters'}</p>
          ) : (
            <div className="space-y-2">
              {filtered.map((item) => {
                const cfg = reasonConfig[item.reason] || reasonConfig[(item as any).status] || reasonConfig.not_started;
                return (
                  <div key={`${item.plan_id}-${item.id}`} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant={cfg.badgeVariant} className="shrink-0 text-xs">
                        {cfg.icon}<span className="ml-1">{cfg.label}</span>
                      </Badge>
                      <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{item.name}</p>
                        <p className="text-xs text-gray-500">{item.customer_org_name} {item.phase_name ? `· ${item.phase_name}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-sm">
                      {(item.aging_days > 0 || item.reason === 'overdue') && (
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Aging</p>
                          <p className={`font-semibold ${item.aging_days > 7 ? 'text-red-600' : item.aging_days > 3 ? 'text-yellow-600' : 'text-gray-700'}`}>
                            {item.aging_days}d
                          </p>
                        </div>
                      )}
                      <div className="text-right w-24 truncate">
                        <p className="text-xs text-gray-400">Owner</p>
                        <p className="text-gray-700">{item.owner_name || 'Unassigned'}</p>
                      </div>
                      {item.due_date && (
                        <div className="text-right">
                          <p className="text-xs text-gray-400">Due</p>
                          <p className={`text-gray-700 ${new Date(item.due_date) < new Date() ? 'text-red-600 font-medium' : ''}`}>
                            {new Date(item.due_date).toLocaleDateString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blocker reasons */}
      {flaggedItems.filter(i => i.blocker_reason).length > 0 && view === 'flagged' && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Blocker Details</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {flaggedItems.filter(i => i.blocker_reason).map((item) => (
                <div key={item.id} className="p-3 border-l-4 border-red-400 bg-red-50 rounded">
                  <p className="font-medium text-sm">{item.name} — {item.customer_org_name}</p>
                  <p className="text-sm text-gray-600 mt-1">{item.blocker_reason}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
